/**
 * Offset of `timeZone` vs UTC at `date`, in minutes (west of GMT positive — same sign as
 * `Date.getTimezoneOffset`). Ported from legacy `global.js` (Intl “lie” trick).
 */
export function getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
	// Abuse the Intl API to get a local ISO 8601 string for a given time zone.
	let iso = date.toLocaleString('en-CA', { timeZone, hour12: false }).replace(', ', 'T');

	// Include the milliseconds from the original timestamp
	iso += '.' + date.getMilliseconds().toString().padStart(3, '0');

	// Lie to the Date object constructor that it's a UTC time.
	const lie = new Date(iso + 'Z');

	// Return the difference in timestamps, as minutes
	return -(lie.getTime() - date.getTime()) / 60 / 1000;
}

/** True when both IANA zones share the same UTC offset at `at` (default: now). */
export function doTimeZonesShareOffset(zoneA: string, zoneB: string, at: Date = new Date()): boolean {
	if (zoneA === zoneB) return true;
	return getTimeZoneOffsetMinutes(at, zoneA) === getTimeZoneOffsetMinutes(at, zoneB);
}

export type BrowserTimezoneSyncDecision =
	| { action: 'none' }
	| { action: 'silent'; browserTimezone: string }
	| { action: 'prompt'; browserTimezone: string; profileTimezone: string };

/**
 * Legacy parity: when profile IANA ≠ browser IANA, always persist the browser zone.
 * Prompt (notice modal) only when offsets differ at `at`; otherwise update silently.
 *
 * A profile with no stored zone adopts the browser's silently. Legacy reached the same
 * outcome by accident — it compared `getTimeZoneOffset(now, undefined)`, which `toLocaleString`
 * resolves to the local zone, so the offsets always matched and no notice was shown.
 *
 * Callers must only pass a `profileTimezone` they have actually loaded: an empty value is read
 * as "this user has no timezone", not as "not fetched yet".
 */
export function resolveBrowserTimezoneSync(
	profileTimezone: string | null | undefined,
	browserTimezone: string,
	at: Date = new Date()
): BrowserTimezoneSyncDecision {
	if (!browserTimezone) {
		return { action: 'none' };
	}

	if (!profileTimezone) {
		return { action: 'silent', browserTimezone };
	}

	if (profileTimezone === browserTimezone) {
		return { action: 'none' };
	}

	if (doTimeZonesShareOffset(browserTimezone, profileTimezone, at)) {
		return { action: 'silent', browserTimezone };
	}

	return { action: 'prompt', browserTimezone, profileTimezone };
}
